<template>
  <VKanbanBoardList>
    <template #header>
      <div class="flex justify-between items-center">
        <h2 class="text-sm font-medium">
          {{ taskStatus }}
        </h2>
        <span
          v-if="meta"
          class="bg-gray-400 text-gray-900 inline-block px-2 text-xs font-normal rounded-full"
        >
          {{ meta.total }}
        </span>
      </div>
    </template>

    <template #default v-if="ids.length">
      <KanbanBoardTaskListLine
        v-for="id in uniqueIds"
        :key="id"
        :id="id"
        :focus="lastEditedTaskId === id"
        @selected-task="$emit('selected-task', $event)"
        @task:updated="notifyTaskUpdated"
      />
      <div v-if="isLoading" class="text-center">
        <Loader small />
      </div>
      <BaseButton v-if="isIdle && hasMore" primary block> Load more </BaseButton>
    </template>

    <template #default v-else>
      <div v-if="isLoading" class="text-center">
        <Loader small />
      </div>
      <div v-else class="p-4 italic text-center text-sm my-2">No Tasks</div>
    </template>
  </VKanbanBoardList>
</template>

<script>
import { uniq } from 'lodash-es';
import VKanbanBoardList from '@/components/Common/VKanbanBoardList.vue';
import KanbanBoardTaskListLine from './KanbanBoardTaskListLine.vue';

const STATUS_IDLE = 'idle';
const STATUS_PENDING = 'pending';
const STATUS_RESOLVED = 'resolved';
const STATUS_REJECTED = 'rejected';

export default {
  name: 'TaskList',

  components: {
    VKanbanBoardList,
    KanbanBoardTaskListLine,
  },

  props: {
    taskStatus: {
      type: String,
      required: true,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    lastEditedTaskId: {
      type: String,
      default: null,
    },
    fetchTasks: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      status: STATUS_IDLE,
      ids: [],
      meta: null,
    };
  },

  computed: {
    isIdle() {
      return this.status === STATUS_IDLE;
    },
    isLoading() {
      return this.status === STATUS_PENDING;
    },
    isResolved() {
      return this.status === STATUS_RESOLVED;
    },
    isRejected() {
      return this.status === STATUS_REJECTED;
    },
    hasMore() {
      return this.meta.current_page < this.meta.last_page;
    },
    uniqueIds() {
      return uniq(this.ids);
    },
  },

  watch: {
    filters() {
      this.refresh();
    },
    meta(newMeta) {
      const { current_page: curPage = 1, last_page: lastPage = 1 } = newMeta || {};

      if (curPage === lastPage) {
        return;
      }

      if (curPage === 1) {
        this.fetchData(2);
      }
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    async fetchData(page = 1, filters = {}) {
      this.status = STATUS_PENDING;

      try {
        const {
          data: { data, meta },
        } = await this.fetchTasks({
          params: {
            page,
            include: [
              'account.logo',
              'userAssigned',
              'project.client.logo',
              'mediaCount',
              'linksCount',
              'notesCount',
            ].join(','),
            filter: {
              ...filters,
              has_project: true,
              status: this.taskStatus,
            },
          },
        });

        await this.persistData(data);

        this.meta = meta;

        this.status = STATUS_RESOLVED;
      } catch (error) {
        this.status = STATUS_REJECTED;
      }
    },

    async persistData(data) {
      try {
        const ids = await this.$store.dispatch('entities/persistEntities', { type: 'tasks', data });

        this.ids = [...this.ids, ...ids];
      } catch (error) {
        /** handle error */
      }
    },

    refresh() {
      this.ids = [];
      this.fetchData(1, this.filters);
    },

    notifyTaskUpdated({ task, oldTask }) {
      if (task.status !== oldTask.status) {
        this.$emit(
          'task-list:updated',
          [task.status, oldTask.status].filter((status) => status)
        );
      }
    },
  },
};
</script>
